/**
 * PageHeader Component
 * Unified header component with flavor support for different pages
 */

import React, { useMemo, useState, useCallback } from 'react'
import { View, Pressable, Platform } from 'react-native'
import { Menu } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { useMenuContext } from '../../src/layout/contexts/MenuContext'
import { getChallengesPageStyles } from '../../src/challenges/styles/challenges.styles'
import { useTheme } from '../../src/theme/useTheme'
import { NotificationBell } from '../notifications/NotificationBell/NotificationBell'
import { NotificationDropdown } from '../notifications/NotificationDropdown/NotificationDropdown'
import { LiveFilterToggle } from '../gameResults/LiveFilterToggle'
import { useNotifications } from '../../src/notifications/hooks/useNotifications'
import { ARENA_PATH } from '../../src/notifications/constants/notifications.constants'
import { EXPAND_CHALLENGE_PARAM } from '../../src/challenges/constants/challenges.constants'
import type { Notification } from '../../src/notifications/types/notifications.types'
import { pageHeaderStyles } from './PageHeader.styles'
import { HEADER_FLAVORS } from './constants/pageHeader.constants'
import { useEconomyStore } from '../../src/shared/stores/economyStore'
import { CreateCoinBalanceChip } from '../create/CreateCoinBalanceChip/CreateCoinBalanceChip'
import { XpHeaderCluster } from '../../src/economy/components/XpHeaderCluster'

const CHALLENGE_NOTIFICATION_TYPES = new Set([
  'CHALLENGE_INVITE',
  'CHALLENGE_ACCEPTED',
  'CHALLENGE_DECLINED',
  'CHALLENGE_CANCELED',
  'CHALLENGE_STARTED',
  'CHALLENGE_REMIND',
  'CHALLENGE_COMPLETED',
])

export type HeaderFlavor = typeof HEADER_FLAVORS[keyof typeof HEADER_FLAVORS]

type PageHeaderProps =
  | { flavor: typeof HEADER_FLAVORS.CHALLENGES; props?: Record<string, never>; leftContent?: React.ReactNode; testID?: string }
  | { flavor: typeof HEADER_FLAVORS.FRIENDS; props?: Record<string, never>; testID?: string }
  | {
      flavor: typeof HEADER_FLAVORS.GAME_RESULTS
      liveFilterOn?: boolean
      onLiveFilterChange?: (on: boolean) => void
      testID?: string
    }

export const PageHeader: React.FC<PageHeaderProps> = (headerProps) => {
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const coins = useEconomyStore((s) => s.coins)
  const coinsLoaded = useEconomyStore((s) => s.coinsLoaded)
  const { handleMenuPress } = useMenuContext()
  const theme = useTheme()
  const challengesPageStyles = useMemo(
    () => getChallengesPageStyles(theme),
    [theme]
  )

  const testID = 'testID' in headerProps ? headerProps.testID : undefined

  const [showNotifications, setShowNotifications] = useState(false)
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications()

  const handleBellPress = useCallback(() => setShowNotifications((prev) => !prev), [])
  const handleCloseNotifications = useCallback(() => setShowNotifications(false), [])
  // Shown on every screen size (native + web, all widths) — the avatar/level/badge is
  // the app's progression anchor, not a phone-only affordance. The 44pt cluster (LV pill
  // overlaps the ring) fits the normal ~60px web / ~44px native row without growing it.
  const headerHeight = insets.top + 4 + 56

  const handleNotificationPress = useCallback(
    (notification: Notification) => {
      if (CHALLENGE_NOTIFICATION_TYPES.has(notification.type)) {
        if (notification.linkId) {
          router.push(`${ARENA_PATH}?${EXPAND_CHALLENGE_PARAM}=${encodeURIComponent(notification.linkId)}`)
        } else {
          router.push(ARENA_PATH)
        }
        requestAnimationFrame(() => {
          handleCloseNotifications()
        })
      } else {
        handleCloseNotifications()
      }
    },
    [handleCloseNotifications, router],
  )

  // Tapping the avatar opens the self-profile (specs/profile-page). Account switching moved
  // to the menu footer, so the avatar is now the "that's me" affordance.
  const handleAvatarPress = useCallback(() => router.push('/app/profile'), [router])
  // Tapping the rank badge opens the Rank Ladder on top of the current screen (specs/rank-ladder).
  const handleRankPress = useCallback(() => router.push('/app/rank-ladder'), [router])

  // XP Ring cluster (specs/xp-and-ranks, header design 1a): the avatar wears the level
  // progress ring, with the LV pill + rank badge/name; press opens the profile.
  const renderHeaderAvatar = () => {
    return <XpHeaderCluster onAvatarPress={handleAvatarPress} onRankPress={handleRankPress} />
  }

  const renderHamburgerMenu = () => (
    <Pressable
      style={({ pressed, hovered }) => [
        challengesPageStyles.hamburgerMenu,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && challengesPageStyles.hamburgerMenuPressed,
        hovered && Platform.OS === 'web' && challengesPageStyles.hamburgerMenuHover,
      ]}
      onPress={handleMenuPress}
      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      testID={testID ? `${testID}-hamburger-menu` : 'page-header-hamburger-menu'}
      accessibilityRole="button"
      accessibilityLabel="Open menu"
    >
      <Menu size={22} color={theme.white} strokeWidth={2.2} />
    </Pressable>
  )

  /**
   * The XP header cluster (avatar + level ring) leads the row, then the existing left
   * content (coin chip or page-specific node). Account switching lives on the avatar tap,
   * so the separate OrgSwitcher chip is gone — it duplicated the avatar in the header.
   */
  const renderLeftCluster = (existing: React.ReactNode) => (
    <View style={pageHeaderStyles.leftCluster}>
      {renderHeaderAvatar()}
      {existing}
    </View>
  )

  const renderRightActions = () => (
    <View style={pageHeaderStyles.rightActions}>
      <NotificationBell unreadCount={unreadCount} onPress={handleBellPress} />
      {renderHamburgerMenu()}
    </View>
  )

  const renderGameResultsRightActions = () => {
    const liveProps =
      headerProps.flavor === HEADER_FLAVORS.GAME_RESULTS &&
      'liveFilterOn' in headerProps &&
      headerProps.onLiveFilterChange != null
        ? {
            liveFilterOn: headerProps.liveFilterOn ?? false,
            onLiveFilterChange: headerProps.onLiveFilterChange,
          }
        : null
    return (
      <View style={pageHeaderStyles.rightActions}>
        {liveProps && (
          <LiveFilterToggle
            value={liveProps.liveFilterOn}
            onValueChange={liveProps.onLiveFilterChange}
            testID={testID ? `${testID}-live-toggle` : 'page-header-live-toggle'}
          />
        )}
        <NotificationBell unreadCount={unreadCount} onPress={handleBellPress} />
        {renderHamburgerMenu()}
      </View>
    )
  }

  const headerContainerStyle = [
    challengesPageStyles.combinedHeader,
    pageHeaderStyles.headerOverride,
    { paddingTop: Platform.OS === 'web' ? insets.top + 4 : insets.top + 4 },
  ]

  // Render based on flavor — bell is in the header for all flavors
  if (headerProps.flavor === HEADER_FLAVORS.CHALLENGES) {
    const leftContent = 'leftContent' in headerProps ? headerProps.leftContent : undefined
    return (
      <>
        <View style={headerContainerStyle} testID={testID || 'page-header-challenges'}>
          {renderLeftCluster(leftContent != null ? leftContent : <CreateCoinBalanceChip coins={coins} loaded={coinsLoaded} />)}
          {renderRightActions()}
        </View>
        {showNotifications && (
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onClose={handleCloseNotifications}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
            onNotificationPress={handleNotificationPress}
            topOffset={headerHeight}
          />
        )}
      </>
    )
  }

  if (headerProps.flavor === HEADER_FLAVORS.FRIENDS) {
    return (
      <>
        <View style={headerContainerStyle} testID={testID || 'page-header-friends'}>
          {renderLeftCluster(<CreateCoinBalanceChip coins={coins} loaded={coinsLoaded} />)}
          {renderRightActions()}
        </View>
        {showNotifications && (
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onClose={handleCloseNotifications}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
            onNotificationPress={handleNotificationPress}
            topOffset={headerHeight}
          />
        )}
      </>
    )
  }

  if (headerProps.flavor === HEADER_FLAVORS.GAME_RESULTS) {
    return (
      <>
        <View style={headerContainerStyle} testID={testID || 'page-header-game-results'}>
          {renderLeftCluster(<CreateCoinBalanceChip coins={coins} loaded={coinsLoaded} />)}
          {renderGameResultsRightActions()}
        </View>
        {showNotifications && (
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onClose={handleCloseNotifications}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
            onNotificationPress={handleNotificationPress}
            topOffset={headerHeight}
          />
        )}
      </>
    )
  }

  return null
}
